import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Check, Users, Globe, Sparkles, ArrowUpRight, ArrowLeft, Info } from "lucide-react";
import { LEADER_TIERS, BRAND_ADDON, BRAND_ADDON_PRICE } from "@/data/siteData";
import WhatsAppIcon from "@/components/site/WhatsAppIcon";

const ICONS = { "in-person-leader": Users, "remote-leader": Globe };
const TIERS = LEADER_TIERS.map((t) => ({ ...t, icon: ICONS[t.id] }));

export default function TechLeaders() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => {
      document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <div className="min-h-screen bg-white text-[#111]">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" data-testid="tl-home-link" className="flex items-center gap-3">
            <span className="w-9 h-9 bg-[#002FA7] text-white font-display text-sm flex items-center justify-center">
              BoT
            </span>
            <div className="leading-none hidden sm:block">
              <div className="font-display text-base tracking-tight">BoT Summit 2026</div>
              <div className="overline text-[10px] text-zinc-500 mt-0.5">Best of Tech</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="tl-back-btn"
              className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase border border-zinc-300 px-4 py-2 hover:bg-black hover:text-white hover:border-black transition-colors"
            >
              <ArrowLeft size={14} /> Back
            </Link>
            <a
              href="mailto:info@botsummits.com?subject=Tech%20Leaders%20of%20Tomorrow%20%E2%80%93%20BoT%20Summit%202026"
              data-testid="tl-contact-btn"
              className="hidden md:inline-flex items-center gap-2 bg-[#002FA7] text-white px-5 py-2.5 text-[12px] font-bold tracking-widest uppercase hover:bg-black transition-colors"
            >
              Nominate →
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="overline text-[#FF2A00] mb-6">/ Tech Leaders of Tomorrow · BoT Summit 2026</div>
          <div className="grid grid-cols-12 gap-y-8 md:gap-8 items-end">
            <div className="col-span-12 lg:col-span-8">
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl uppercase tracking-tighter leading-[0.9]">
                Recognise the
                <br />
                <span className="text-[#002FA7]">next generation</span> of tech leaders.
              </h1>
            </div>
            <div className="col-span-12 lg:col-span-4">
              <p className="text-zinc-700 text-base leading-relaxed">
                BoT Summit 2026 honours high-potential operators, founders and CXO's
                as Global Transformational Tech Leaders — felicitated on the main stage in front of
                500+ CIOs, CTOs and policymakers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tiers */}
      <section className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="overline text-zinc-500">/ 02 tiers</div>
              <h2 className="font-display text-3xl md:text-4xl uppercase tracking-tight mt-2">
                Choose a recognition tier.
              </h2>
            </div>
            <div className="hidden md:block overline text-zinc-400 text-right">
              Nominations reviewed on a rolling basis.
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border-t border-l border-zinc-200">
            {TIERS.map((t, i) => {
              const Icon = t.icon;
              return (
                <article
                  key={t.id}
                  id={t.id}
                  data-testid={`tl-tier-${t.id}`}
                  className={`scroll-mt-24 border-r border-b border-zinc-200 p-8 md:p-10 relative flex flex-col ${
                    t.highlighted ? "bg-[#0A0A0A] text-white" : "bg-white"
                  }`}
                >
                  {t.highlighted && (
                    <div className="absolute top-0 right-0 bg-[#FF2A00] text-white px-3 py-1 overline text-[10px]">
                      Recommended
                    </div>
                  )}
                  <div className="flex items-start justify-between">
                    <Icon size={32} style={{ color: t.highlighted ? "#6E85FF" : t.color }} strokeWidth={1.5} />
                    <span className={`overline ${t.highlighted ? "text-zinc-400" : "text-zinc-500"}`}>
                      0{i + 1} / 02
                    </span>
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl uppercase tracking-tighter mt-8">
                    {t.name}
                  </h3>
                  {t.category && (
                    <div className={`font-mono text-xs mt-2 ${t.highlighted ? "text-white/80" : "text-zinc-700"}`}>
                      {t.category}
                    </div>
                  )}
                  <div className={`overline mt-2 ${t.highlighted ? "text-zinc-400" : "text-zinc-500"}`}>
                    {t.badge}
                  </div>
                  <div className="mt-6 flex items-baseline gap-2" data-testid={`tl-price-${t.id}`}>
                    <span className="font-display text-5xl tracking-tighter" style={{ color: t.highlighted ? "#6E85FF" : t.color }}>
                      {t.price}
                    </span>
                    <span className={`overline ${t.highlighted ? "text-zinc-400" : "text-zinc-500"}`}>{t.priceNote}</span>
                  </div>

                  <div className={`mt-8 pt-8 border-t ${t.highlighted ? "border-zinc-800" : "border-zinc-200"} flex-1`}>
                    <div className={`overline mb-5 ${t.highlighted ? "text-zinc-400" : "text-zinc-500"}`}>
                      Deliverables
                    </div>
                    <ul className="space-y-3">
                      {t.deliverables.map((d, k) => (
                        <li key={k} className="flex gap-3 text-sm leading-relaxed">
                          <Check
                            size={16}
                            className="shrink-0 mt-0.5"
                            style={{ color: t.highlighted ? "#6E85FF" : t.color }}
                          />
                          <span className={t.highlighted ? "text-zinc-200" : "text-zinc-700"}>
                            {d}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <a
                    href={`mailto:info@botsummits.com?subject=${encodeURIComponent(`Tech Leaders of Tomorrow - ${t.name} Nomination`)}`}
                    data-testid={`tl-cta-${t.id}`}
                    className={`mt-10 inline-flex items-center justify-center gap-2 w-full px-7 py-4 text-xs font-bold tracking-[0.25em] uppercase transition-colors ${
                      t.highlighted
                        ? "bg-white text-black hover:bg-[#FF2A00] hover:text-white"
                        : "bg-[#002FA7] text-white hover:bg-black"
                    }`}
                  >
                    Nominate now <ArrowUpRight size={16} />
                  </a>
                  <Link
                    to={`/international-pay?option=${encodeURIComponent(t.name)}`}
                    data-testid={`tl-pay-${t.id}`}
                    className={`mt-3 inline-flex items-center justify-center gap-2 w-full px-7 py-4 text-xs font-bold tracking-[0.25em] uppercase border transition-colors ${
                      t.highlighted
                        ? "border-white/40 text-white hover:bg-[#FF2A00] hover:border-[#FF2A00]"
                        : "border-[#002FA7] text-[#002FA7] hover:bg-[#FF2A00] hover:border-[#FF2A00] hover:text-white"
                    }`}
                  >
                    Pay {t.price} online <ArrowUpRight size={16} />
                  </Link>
                </article>
              );
            })}
          </div>

          <div data-testid="tl-brand-addon" className="mt-10 border border-black p-8 md:p-10 bg-zinc-50">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <Sparkles size={24} className="text-[#FF2A00]" strokeWidth={1.5} />
                <h3 className="font-display text-2xl md:text-3xl uppercase tracking-tighter">Brand add-on</h3>
              </div>
              <div className="text-right">
                <div className="font-display text-4xl tracking-tighter text-[#FF2A00]" data-testid="tl-brand-addon-price">
                  +${BRAND_ADDON_PRICE} <span className="overline text-zinc-500 align-middle">USD</span>
                </div>
                <div className="overline text-zinc-500 mt-1">Optional · In Person tier only</div>
              </div>
            </div>
            <p className="mt-3 text-sm text-zinc-600 max-w-2xl">
              Amplify your company alongside the leader's felicitation.
            </p>
            <ul className="mt-6 grid md:grid-cols-2 gap-x-8 gap-y-3">
              {BRAND_ADDON.map((d) => (
                <li key={d} className="flex gap-3 text-sm leading-relaxed text-zinc-700">
                  <Check size={16} className="shrink-0 mt-0.5 text-[#FF2A00]" />
                  {d}
                </li>
              ))}
            </ul>
            <div
              data-testid="tl-brand-addon-exhibition-note"
              className="mt-8 flex gap-3 border-l-4 border-[#FF2A00] bg-[#FF2A00]/10 px-5 py-4"
            >
              <Info size={18} className="shrink-0 mt-0.5 text-[#FF2A00]" />
              <p className="text-sm font-semibold leading-relaxed text-zinc-900">
                Please note that additional charges will apply for reserving on-ground exhibition space for
                product displays or stall setup at the venue.
              </p>
            </div>
            <a
              href={`mailto:info@botsummits.com?subject=${encodeURIComponent("Tech Leaders of Tomorrow - Brand add-on")}`}
              data-testid="tl-cta-brand-addon"
              className="mt-8 inline-flex items-center gap-2 bg-black text-white px-7 py-4 text-xs font-bold tracking-[0.25em] uppercase hover:bg-[#FF2A00] transition-colors"
            >
              Ask about the brand add-on <ArrowUpRight size={16} />
            </a>
            <Link
              to={`/international-pay?option=${encodeURIComponent("In Person - Leader Participation")}&addon=1`}
              data-testid="tl-pay-brand-addon"
              className="mt-8 ml-0 sm:ml-3 inline-flex items-center gap-2 border border-black px-7 py-4 text-xs font-bold tracking-[0.25em] uppercase hover:bg-[#FF2A00] hover:border-[#FF2A00] hover:text-white transition-colors"
            >
              Pay online with In Person <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Custom */}
      <section className="border-b border-zinc-200 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="grid grid-cols-12 gap-y-8 md:gap-8">
            <div className="col-span-12 md:col-span-5">
              <div className="overline text-[#FF2A00]">/ Questions?</div>
              <h2 className="font-display text-3xl md:text-4xl uppercase tracking-tight mt-3">
                Need help choosing?
              </h2>
            </div>
            <div className="col-span-12 md:col-span-7">
              <p className="text-zinc-700 leading-relaxed">
                Choose In Person to be felicitated on the main stage and join the summit, or
                Remote to receive the same recognition without travelling. Companies choosing
                In Person can add the Brand add-on for brand placement and a speaker slot.
                Write to us and we'll help you pick the right fit.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <a
                  href="mailto:info@botsummits.com?subject=Tech%20Leaders%20of%20Tomorrow%20-%20BoT%20Summit%202026"
                  className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-7 py-4 text-xs font-bold tracking-[0.25em] uppercase hover:bg-black transition-colors"
                >
                  info@botsummits.com
                </a>
                <a
                  href="https://wa.me/447915361665"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="techleaders-whatsapp-uk"
                  className="inline-flex items-center gap-2 border border-black text-black px-7 py-4 text-xs font-bold tracking-[0.25em] uppercase hover:bg-black hover:text-white transition-colors"
                >
                  <WhatsAppIcon size={14} /> +44 7915361665
                </a>
                <a
                  href="https://wa.me/918825035701"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="techleaders-whatsapp-india"
                  className="inline-flex items-center gap-2 border border-black text-black px-7 py-4 text-xs font-bold tracking-[0.25em] uppercase hover:bg-black hover:text-white transition-colors"
                >
                  <WhatsAppIcon size={14} /> +91 8825035701
                </a>
                <a
                  href="https://wa.me/971525330661"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="techleaders-whatsapp-uae"
                  className="inline-flex items-center gap-2 border border-black text-black px-7 py-4 text-xs font-bold tracking-[0.25em] uppercase hover:bg-black hover:text-white transition-colors"
                >
                  <WhatsAppIcon size={14} /> +971 525330661
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-[#0A0A0A] text-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="overline text-zinc-500">© 2026 BoT Summit · Best of Tech</div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 overline text-zinc-500">
            <Link to="/" className="hover:text-white">Home</Link>
            <Link to="/sponsorship" className="hover:text-white">Sponsorship</Link>
            <Link to="/payment-terms" className="hover:text-white">Payment Terms</Link>
            <Link to="/code-of-conduct" className="hover:text-white">Code of Conduct</Link>
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
